import React, { ChangeEvent, useCallback, useMemo } from 'react'
import { find, keyBy } from 'lodash'
import { useTranslation } from 'react-i18next'
import { ensureArray } from 'cityads-v3/utils/arrayUtils'
import { stringOrNumber } from 'cityads-v3/utils/textUtils'

import Checkbox from '@material-ui/core/Checkbox'
import CircularProgress from '@material-ui/core/CircularProgress'
import ClearIcon from '@material-ui/icons/ClearOutlined'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import InputAdornment from '@material-ui/core/InputAdornment'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemText from '@material-ui/core/ListItemText'
import SearchIcon from '@material-ui/icons/SearchOutlined'
import TextField, { TextFieldProps } from '@material-ui/core/TextField'

import classes from './SelectField.scss'

type DictionaryItem<T> = {
  disabled?: boolean,
  name: string,
  value: T
}

type SelectFieldProps<T = string, K = string> = TextFieldProps & {
  items: DictionaryItem<K>[],
  additionalDictionary?: DictionaryItem<K>[],
  alwaysShowSelected?: boolean,
  disabledValues?: K[],
  multiple?: boolean,
  dense?: boolean,
  handleChange(value: T | React.Dispatch<React.SetStateAction<T>> | ChangeEvent): void,
  minLength?: number,
  value: T,
  withClear?: boolean,
  searchProps?: TextFieldProps & {
    isLoading?: boolean
    frontFilter?: boolean
  }
}

export const SelectField = <T, K>({
  items,
  additionalDictionary = [],
  alwaysShowSelected = false,
  disabledValues = [],
  handleChange,
  minLength = 0,
  multiple = false,
  dense = false,
  searchProps,
  withClear = false,
  ...rest
}: React.PropsWithChildren<SelectFieldProps<T, K>>): React.ReactElement => {
  const { t } = useTranslation(['main'])
  const { isLoading: searchIsLoading = false, frontFilter, ...search } = searchProps ?? {}

  const handleClear = useCallback(() => {
    if (multiple) {
      handleChange([] as unknown as T)
    } else {
      handleChange('' as unknown as T)
    }
  }, [handleChange, multiple])

  const handleSearchKeyDown = useCallback((evt: React.KeyboardEvent<HTMLInputElement>) => {
    evt.stopPropagation()
  }, [])

  const handleSearchClick = useCallback((evt: React.MouseEvent<HTMLInputElement>) => {
    evt.stopPropagation()
  }, [])

  const dictionary = useMemo(() => ({
    ...items,
    ...additionalDictionary
  }), [
    items,
    additionalDictionary
  ])

  const dictionaryItemsByValue = useMemo(() => keyBy(dictionary, 'value'), [dictionary]);

  const alwaysShowItems = useMemo(() => (
    (alwaysShowSelected ?
      (rest.value as unknown as K[])
        .filter(value => !items.map(item => item.value).includes(value))
        .map(id => dictionaryItemsByValue[id as unknown as number] ?? { value: id, title: '' } as unknown as DictionaryItem<K>)
      : [])
  ), [
    rest.value,
    items,
    dictionaryItemsByValue
  ])

  const showClearButton = withClear && !minLength && !rest.disabled && (Array.isArray(rest.value) ? rest.value.length : rest.value);
  const value = useMemo(() => ensureArray(rest.value), [rest.value])

  const filteredItems = useMemo(() => {
    if (!searchProps || !searchProps?.value || !searchProps.frontFilter) { return items; }
    return items.filter(item => item.name.toLowerCase().includes(String(searchProps.value).toLowerCase()))
  }, [
    items,
    searchProps
  ])

  return (
    <TextField
      select
      SelectProps={{
        multiple,
        renderValue: multiple ?
          (selected: number[]) => selected
            .map(value => (find(dictionary, { value }) as { name: string })?.name)
            .filter(Boolean)
            .join(', ')
          :
          (selected: T) => (find(dictionary, { value: selected }) as { name: string })?.name
      }}
      onChange={(evt) => {
        const value = (evt as unknown as React.ChangeEvent<{ value: T }>).target.value;

        if (minLength && ensureArray(value).length < minLength) {
          return;
        }

        handleChange?.(value)
      }}
      InputProps={{
        endAdornment: showClearButton ? (
          <InputAdornment position="end">
            <IconButton classes={{ root: classes.clearButton }} onClick={handleClear}>
              <ClearIcon />
            </IconButton>
          </InputAdornment>
        ) : null
      }}
      helperText={minLength ? `${t('min')}: ${minLength}` : null}
      value={''}
      {...rest}
    >
      {searchProps && (
        <div className={classes.searchContainer}>
          <TextField
            fullWidth
            placeholder={t('search')}
            onClick={handleSearchClick}
            onKeyDown={handleSearchKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" disablePointerEvents>
                  {searchIsLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <SearchIcon />
                  )}
                </InputAdornment>
              )
            }}
            {...search}
          />
        </div>
      )}

      {filteredItems.map(item => (
        <MenuItem
          dense={dense}
          key={stringOrNumber(item.value)}
          value={stringOrNumber(item.value)}
          disabled={
            item.disabled ||
            value.includes(item.value as unknown as T)
            && value.length === minLength
            || disabledValues.includes(item.value)
          }
        >
          {multiple && <Checkbox color="primary" checked={value.includes(item.value as unknown as T)} className={classes.checkbox} />}
          <ListItemText primary={item.name} classes={{ primary: classes.listItemPrimary }} />
        </MenuItem>
      ))}

      {alwaysShowItems.length ? <Divider /> : null}
      {alwaysShowItems.map(item => (
        <MenuItem
          dense={dense}
          key={stringOrNumber(item.value)}
          value={stringOrNumber(item.value)}
          disabled={
            value.includes(item.value as unknown as T)
            && value.length === minLength
            || disabledValues.includes(item.value)
          }
        >
          {multiple && <Checkbox color="primary" checked={value.includes(item.value as unknown as T)} className={classes.checkbox} />}
          <ListItemText primary={item.name} classes={{ primary: classes.listItemPrimary }} />
        </MenuItem>
      ))}
    </TextField>
  )
}

export default SelectField;
